import { HeaderState } from 'models/HeaderState';
import {
  CartEvent,
  ClearEcommerceEvent,
  GTM_LIST_NAME,
  RemoveFromCartEvent
} from 'store/googleTagManager/types';
import { mapCartItemToGtm } from './utils';
import { getClearEcommerceEvent } from 'store/googleTagManager/events/genericEvents';
import { GtmCartItemAction } from 'store/googleTagManager/actions';

/**
 * Event for when a product has been added to the cart.
 * Returns an array with a ClearEcommerceEvent and AddToCartEvent.
 *
 * Events containing an Ecommerce object should have a ClearEcommerceEvent before it.
 */
export const getAddToCartEvent = (
  action: GtmCartItemAction,
  state: HeaderState
): [ClearEcommerceEvent, CartEvent] | undefined => {
  const { cartItem, itemListName, btnLocation, amount = 1, addons } = action;

  // quantity is read-only, so need to do a bit more work to change amount
  const addedCartItem = { ...cartItem, quantity: { ...cartItem.quantity, amount: amount } };

  const addedGtmAddons =
    addons?.map(addon =>
      mapCartItemToGtm({
        cartItem: { ...addon, quantity: { ...addon.quantity, amount: amount } },
        itemListName: itemListName ?? GTM_LIST_NAME.ADDON
      })
    ) || [];

  const mainAddedItem = mapCartItemToGtm({
    cartItem: addedCartItem,
    addToCartLocation: btnLocation,
    itemListName: itemListName,
    useNewCart: state.appShell.shouldUseNewCart
  });

  const items = [mainAddedItem, ...addedGtmAddons];

  const value = parseFloat(
    items.reduce((sum, { quantity, price }) => sum + quantity * price, 0).toFixed(2)
  );

  return [
    getClearEcommerceEvent(),
    {
      event: 'add_to_cart',
      ecommerce: {
        currency: state.appShell.currencyCode,
        value: value,
        items: items
      }
    }
  ];
};

export const getRemoveFromCartEvent = (
  { itemListName, cartItem, amount = 1, addons }: GtmCartItemAction,
  prevState: HeaderState
): [ClearEcommerceEvent, RemoveFromCartEvent] | undefined => {
  const { appShell } = prevState;

  // quantity is read-only, so need to do a bit more work to change amount
  const removedCartItem = { ...cartItem, quantity: { ...cartItem.quantity, amount: amount } };

  const removedGtmAddons =
    addons?.map(addon =>
      mapCartItemToGtm({
        cartItem: { ...addon, quantity: { ...addon.quantity, amount: amount } },
        itemListName: itemListName
      })
    ) || [];

  const removedGtmProduct = mapCartItemToGtm({
    cartItem: removedCartItem,
    itemListName: itemListName,
    useNewCart: appShell.shouldUseNewCart
  });

  const items = [removedGtmProduct, ...removedGtmAddons];

  return [
    getClearEcommerceEvent(),
    {
      event: 'remove_from_cart',
      ecommerce: {
        currency: appShell.currencyCode,
        items: items
      }
    }
  ];
};
